import {
  API_CONTACT_SECRET,
  API_INBOUND_SECRET,
  BASE_MEDIA_URL,
  PASSWORD,
} from './apiHooks';
import { KeyTopicItem } from '@composites/PartnerProspectusPdfContent/components/KeyTopics';
import dayjs from 'dayjs';

export const getImageSrc = (input?: string, portfolioSlug?: string): string => {
  if (typeof input === undefined || input === null || !input?.length) {
    return '';
  }

  const insertPortfolio = (pathname: string) => {
    const parts = pathname.split('/').filter(Boolean); // remove empty segments
    const imagesIndex = parts.findIndex(p => p === 'images');

    if (imagesIndex === -1 || parts.length <= imagesIndex + 1) {
      // if no 'images' or no first folder after images, return original
      return pathname.startsWith('/') ? pathname : `/${pathname}`;
    }

    // Insert 'portfolios/<portfolioSlug>' after the first folder under 'images'
    const firstFolderIndex = imagesIndex + 1;
    const newParts = [
      ...parts.slice(imagesIndex + 1, firstFolderIndex + 1), // include /images/<firstFolder>
      'portfolios',
      portfolioSlug,
      ...parts.slice(firstFolderIndex + 1), // remaining subfolders + filename
    ];

    // Remove the leading "images" from the final result
    return '/' + newParts.join('/');
  };

  if (input.startsWith('http')) {
    if (!portfolioSlug) return input;

    try {
      const url = new URL(input);
      const newPath = insertPortfolio(url.pathname);
      return `${url.protocol}//${url.host}${newPath}`;
    } catch {
      return input;
    }
  }

  const newLink = input
    ?.split('users')
    ?.join('profiles')
    ?.split('/profiles/')
    .join('-');
  return `${BASE_MEDIA_URL}/${newLink}`;
};

export function parsePath(path: string) {
  // Remove trailing slash if present
  const cleaned = path.replace(/\/$/, '');

  // Split into parts (ignoring the first empty string from leading slash)
  const parts = cleaned.split('/').filter(Boolean);

  // If only one segment, it's all base
  if (parts.length === 1) {
    return {
      base: '/' + parts[0],
      slug: undefined,
    };
  }

  // Otherwise: base = everything except last, slug = last
  const slug = parts.pop();
  const base = '/' + parts.join('/');

  return { base, slug };
}

export const getFullLinkedInHttpLink = (link?: string): string => {
  if (!link) {
    return '';
  }
  if (link?.includes('linkedin')) {
    return link;
  } else {
    return 'https://www.linkedin.com/in/' + link;
  }
};

export const getFullTwitterHttpLink = (link?: string): string => {
  if (!link) {
    return '';
  }

  if (link?.includes('twitter')) {
    return link;
  } else {
    return 'https://twitter.com/' + link;
  }
};

export const getFullInstagramHttpLink = (link?: string): string => {
  if (!link) {
    return '';
  }

  if (link?.includes('instagram')) {
    return link;
  } else {
    return 'https://instagram.com/' + link;
  }
};

export const getFullXingHttpLink = (link?: string): string => {
  if (!link) {
    return '';
  }
  if (link?.includes('xing')) {
    return link;
  } else {
    return 'https://www.xing.com/profile/' + link;
  }
};

export const getFullFacebookHttpLink = (link?: string): string => {
  if (!link) {
    return '';
  }
  if (link?.includes('facebook')) {
    return link;
  } else {
    return 'https://www.facebook.com/' + link;
  }
};

export const formatWebsiteLink = (website?: string) => {
  if (!website) {
    return '';
  }
  if (website?.length > 0) {
    if (!website.startsWith('https://') && !website.startsWith('http://'))
      return `https://${website}`;
    else return website;
  }
  return '';
};

export const extractSearchParams = (searchParams: URLSearchParams) => {
  let searchObj = {};
  searchParams.forEach((value, key) => {
    searchObj = { ...searchObj, [key]: value };
  });
  return searchObj;
};

export const capitalizeFirstLetter = (S: string) => {
  if (S?.length === 0) return '';
  return S?.charAt(0).toUpperCase() + S?.slice(1);
};

export const generateFilterOptions = (options: string[]) => {
  return [...new Set(options)]?.map(option => ({
    key: option,
    value: option,
  }));
};

export interface PrerecordedVideo {
  image: string;
  videoReference: string;
  videoType: string;
}

export const getEmbedVimeo = (url: string) => {
  if (
    url?.includes('/embedparameter/') ||
    url?.endsWith('/embed') ||
    url?.includes('?h=') ||
    url?.includes('player.vimeo.com')
  )
    return url;
  else if (url?.includes('/event/')) return `${url}/embed`;
  else if (url?.includes('vimeo.com')) {
    const urlPathname = new URL(url)?.pathname
      ?.split('/')
      .filter(e => e?.length);
    if (urlPathname?.length === 1)
      return `https://player.vimeo.com/video/${urlPathname?.[0]}?embedparameter=${urlPathname?.[0]}`;
    if (urlPathname?.length === 2)
      return `https://player.vimeo.com/video/${urlPathname?.[0]}?h=${urlPathname?.[1]}`;
  }
  return `https://player.vimeo.com/video/${url}?embedparameter=${url}`;
};

export const getPrerecordedVideoUrl = (prerecorded: PrerecordedVideo) => {
  if (prerecorded?.videoType === 'youtube') {
    if (prerecorded.videoReference?.includes('/embed/'))
      return prerecorded.videoReference;
    return `https://www.youtube.com/embed/${prerecorded.videoReference}`;
  }
  if (prerecorded?.videoType === 'vimeo')
    return getEmbedVimeo(prerecorded.videoReference);

  return prerecorded.videoReference;
};

export const applyAndToArray = (array: boolean[]): boolean => {
  return array.reduce((acc: boolean, curr: boolean) => acc && curr, true);
};

export const findNearestValue = (numbers: number[], target: number) => {
  return numbers.reduce((nearest, num) =>
    Math.abs(num - target) < Math.abs(nearest - target) ? num : nearest
  );
};

export const getSpeakerFullName = (
  firstName: string | undefined,
  lastName: string | undefined
) => [firstName, lastName]?.filter(e => e?.length).join(' ');

export const groupAndSortByDate = <
  T extends Record<K, string>,
  K extends keyof T,
>(
  array: T[],
  key: K
): Record<string, T[]> => {
  const grouped = array.reduce<Record<string, T[]>>((result, currentValue) => {
    const groupKey = dayjs(currentValue[key]).format('DD-MM-YYYY');

    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(currentValue);

    return result;
  }, {});

  for (const groupKey in grouped) {
    grouped[groupKey].sort((a, b) => {
      const dateA = dayjs(a[key]);
      const dateB = dayjs(b[key]);

      if (dateA.isBefore(dateB)) {
        return -1;
      } else if (dateA.isAfter(dateB)) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  return grouped;
};

export const groupAndSortByDateTimezoneIndependent = <
  T extends Record<K, string>,
  K extends keyof T,
>(
  array: T[],
  key: K
): Record<string, T[]> => {
  const grouped = array.reduce<Record<string, T[]>>((result, currentValue) => {
    // Extract the date part (YYYY-MM-DD) and reformat to DD-MM-YYYY
    const rawDate = currentValue[key]?.split('T')[0];
    if (!rawDate) return result;

    const groupKey = rawDate.split('-').reverse().join('-'); // "DD-MM-YYYY"

    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(currentValue);

    return result;
  }, {});

  // Sort each group by original ISO date (still timezone-safe)
  for (const groupKey in grouped) {
    grouped[groupKey].sort((a, b) => {
      const dateA = a[key];
      const dateB = b[key];

      // Direct string comparison works for ISO dates
      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
    });
  }

  return grouped;
};

export const getUrl = (url: string) => url.substring(1);

export function uniqBy<T extends Record<string, any>>(
  array: T[],
  key: keyof T
): T[] {
  const seen = new Map<T[keyof T], T>();
  array.forEach(item => {
    if (!seen.has(item[key])) {
      seen.set(item[key], item);
    }
  });
  return Array.from(seen.values());
}

export const safeJSONParse = (value: string | null | undefined) => {
  try {
    return JSON.parse(value ?? '{}');
  } catch (error) {
    return {};
  }
};

export const absoluteFilePath = (relativePath: string) => {
  const strippedPath = relativePath.split('/').slice(1).join('/');
  return `https://media.we-conect.com/${strippedPath}`;
};

export const getYoutubeVideoId = (url: string) => {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

export const getYouTubeThumbnail = (video: {
  id: string;
  link: string;
  title: string;
}) => {
  try {
    const youtubeLinkId = getYoutubeVideoId(video.link);

    if (youtubeLinkId) {
      return `https://img.youtube.com/vi/${youtubeLinkId}/sddefault.jpg`; // Get default thumbnail
    } else {
      return true;
    }
  } catch (e) {
    return true;
  }
};

export const truncateAfterLastBrace = (input: string): string => {
  const lastBraceIndex = input.lastIndexOf('}');
  if (lastBraceIndex === -1) {
    return input;
  }
  return input.substring(0, lastBraceIndex + 1);
};

export function joinLastWithAmpersand(arr: string[]) {
  if (!arr || arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  return arr.slice(0, -1).join(', ') + ' & ' + arr[arr.length - 1];
}

export const splitArrayBySubtitleLengthFunctional = (
  array: KeyTopicItem[],
  maxLength: number
): KeyTopicItem[][] => {
  return array.reduce(
    (acc: KeyTopicItem[][], item) => {
      const currentChunk = acc[acc.length - 1];
      const currentLength = currentChunk.reduce(
        (sum, i: KeyTopicItem) => sum + Number(i.subtitle.length) + 100,
        0
      );

      if (currentLength + item.subtitle.length > maxLength) {
        acc.push([item]);
      } else {
        currentChunk.push(item);
      }
      return acc;
    },
    [[]]
  );
};

export const formatCurrency = (value: number, currency = 'eur') => {
  let locale = 'de-DE';

  if (currency === 'usd') {
    locale = 'en-US';
  } else if (currency === 'gbp') {
    locale = 'en-GB';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
};

export const generatePasswordInbound = async (
  email: string
): Promise<string> => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const envPass = API_INBOUND_SECRET;
  const rawPass = envPass + email + today;

  const encoder = new TextEncoder();
  const data = encoder.encode(rawPass);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

export const generatePasswordContact = async (
  email: string
): Promise<string> => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const envPass = API_CONTACT_SECRET;
  const rawPass = envPass + email + today;

  const encoder = new TextEncoder();
  const data = encoder.encode(rawPass);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

export const toNumber = (val: any): number => {
  if (typeof val === 'number') return val;
  if (val == null) return 0;
  const cleaned = String(val).replace(/[^0-9.,-]/g, '');
  const normalized = cleaned.replace(/,/g, '');
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? 0 : n;
};
